import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';

/**
 * Select-all button group that allows users to select all in different contexts
 * @param {function} props.selectAllHandler  button click handler for select-all action
 * @param {function} props.selectAllExpiredHandler button click handler for select-all-expired action
 * @param {function} props.selectAllExpiringHandler button click handler for select-all-expiring-soon action
 * @returns button group with options
 */
const SelectAllButtonGroup = ({
	selectAllHandler,
	selectAllExpiredHandler,
	selectAllExpiringHandler,
	sRowsPerPage,
}) => {
	const [sOpen, setOpen] = useState(false);
	const anchorRef = useRef(null);
	const [selectedIndex, setSelectedIndex] = useState(1);
	const options = [
		`Select all (${sRowsPerPage})`,
		'Overdue',
		'Expiring within 30 days',
	];

	const handleButtonClick = () => {
		const mockEvent = { target: { checked: true } };
		switch (selectedIndex) {
			case 0:
				selectAllHandler(mockEvent);
				break;
			case 1:
				selectAllExpiredHandler(mockEvent);
				break;
			case 2:
				selectAllExpiringHandler(mockEvent);
				break;
			default:
				break;
		}
	};

	const handleMenuItemClick = (event, index) => {
		setSelectedIndex(index);
		setOpen(false);
	};

	const handleToggle = () => {
		setOpen(prevOpen => !prevOpen);
	};

	const handleClose = event => {
		if (anchorRef.current && anchorRef.current.contains(event.target)) {
			return;
		}

		setOpen(false);
	};

	return (
		<>
			<ButtonGroup
				variant='contained'
				ref={anchorRef}
				aria-label='Button group with a nested menu'
			>
				<Button onClick={handleButtonClick}>
					{options[selectedIndex]}
				</Button>
				<Button
					size='small'
					aria-controls={sOpen ? 'split-button-menu' : undefined}
					aria-expanded={sOpen ? 'true' : undefined}
					aria-label='select merge strategy'
					aria-haspopup='menu'
					onClick={handleToggle}
				>
					<ArrowDropDownIcon />
				</Button>
			</ButtonGroup>
			<Popper
				sx={{ zIndex: 3 }}
				open={sOpen}
				anchorEl={anchorRef.current}
				role={undefined}
				transition
				disablePortal
			>
				{({ TransitionProps, placement }) => (
					<Grow
						{...TransitionProps}
						style={{
							transformOrigin:
								placement === 'bottom'
									? 'center top'
									: 'center bottom',
						}}
					>
						<Paper sx={{ marginLeft: 7 }}>
							<ClickAwayListener onClickAway={handleClose}>
								<MenuList id='split-button-menu' autoFocusItem>
									{options.map((option, index) => (
										<MenuItem
											key={option}
											selected={index === selectedIndex}
											onClick={event =>
												handleMenuItemClick(
													event,
													index
												)
											}
										>
											{option}
										</MenuItem>
									))}
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</>
	);
};

SelectAllButtonGroup.propTypes = {
	selectAllHandler: PropTypes.func.isRequired,
	selectAllExpiredHandler: PropTypes.func.isRequired,
	selectAllExpiringHandler: PropTypes.func.isRequired,
};

export default SelectAllButtonGroup;
